import { Button } from "@/components/ui/UIElements/Button";
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  useToast,
  useCreateFranchise,
  useAssignBusinessToFranchise,
  useBusinesses,
} from "@finitless/shared/hooks";
import {
  handleApiError,
  handleApiSuccess,
  stringToSlug,
} from "@finitless/shared/lib";
import { TIMEZONES } from "@finitless/shared/constants";
import { NativeSelect } from "@/components/ui/UIElements/Select";
import { Icon, Modal } from "../";
import BusinessesTab from "./BusinessesTab";

const CreateBrandModal = ({ isOpen, onClose }) => {
  const toast = useToast();
  const createMutation = useCreateFranchise();

  const [currentStep, setCurrentStep] = useState(1); // 1: Create Brand, 2: Assign Businesses
  const [createdFranchiseId, setCreatedFranchiseId] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    internal_name: "",
    timezone: TIMEZONES?.[0] ?? "UTC",
    logo: null,
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const logoReaderRef = useRef(null);
  const logoObjectUrlRef = useRef(null);
  const cancelLogoRead = useCallback(() => {
    const reader = logoReaderRef.current;
    logoReaderRef.current = null;
    if (reader?.readyState === 1) reader.abort();
  }, []);
  const releaseLogoPreview = useCallback(() => {
    if (logoObjectUrlRef.current) {
      URL.revokeObjectURL(logoObjectUrlRef.current);
      logoObjectUrlRef.current = null;
    }
  }, []);
  useEffect(
    () => () => {
      cancelLogoRead();
      releaseLogoPreview();
    },
    [cancelLogoRead, releaseLogoPreview],
  );
  useEffect(() => {
    if (!isOpen) {
      cancelLogoRead();
      releaseLogoPreview();
      setLogoPreview(null);
    }
  }, [isOpen, cancelLogoRead, releaseLogoPreview]);

  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedBusinessIds, setSelectedBusinessIds] = useState(new Set());

  const { data: businessesData, isLoading: isLoadingBusinesses } =
    useBusinesses({
      enabledFilter: "all",
      searchTerm,
      page: currentPage,
      pageSize: 10,
    });

  const businesses = businessesData?.businesses || [];
  const pagination = businessesData?.pagination || {};

  const assignBusinessMutation = useAssignBusinessToFranchise();

  // Slug generator (shared util)
  const generateSlug = (name) => stringToSlug(name);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "name") {
      const slug = generateSlug(value);
      setFormData((prev) => ({
        ...prev,
        name: value,
        slug: slug,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }

      if (file.size > 512 * 1024) {
        toast.error("Image size must be less than 512KB");
        return;
      }

      cancelLogoRead();
      const reader = new FileReader();
      logoReaderRef.current = reader;
      reader.onload = (event) => {
        if (logoReaderRef.current !== reader) return;
        logoReaderRef.current = null;
        const base64String = event.target.result;
        releaseLogoPreview();
        const previewUrl = URL.createObjectURL(file);
        logoObjectUrlRef.current = previewUrl;
        setLogoPreview(previewUrl);
        setFormData((prev) => ({
          ...prev,
          logo: base64String,
        }));
      };
      reader.onerror = () => {
        if (logoReaderRef.current !== reader) return;
        logoReaderRef.current = null;
        toast.error("Error reading image file");
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("Brand name is required.");
      return;
    }

    if (!formData.slug.trim()) {
      toast.error("Brand slug is required.");
      return;
    }

    try {
      const values = {
        name: formData.name,
        slug: formData.slug || null,
        internal_name: formData.internal_name || null,
        timezone: formData.timezone || null,
      };

      if (!formData.internal_name) {
        delete values.internal_name;
      }

      if (formData.logo) {
        values.logo = formData.logo;
      }

      const result = await createMutation.mutateAsync(values);

      handleApiSuccess("Brand created successfully", toast, {
        title: "Success",
      });

      setCreatedFranchiseId(result.id);
      setCurrentStep(2);
    } catch (error) {
      handleApiError(error, toast, {
        title: "Failed to Create Brand",
        defaultMessage: "Failed to create brand. Please try again.",
      });
    }
  };

  const handleCancel = () => {
    cancelLogoRead();
    releaseLogoPreview();
    setFormData({
      name: "",
      slug: "",
      internal_name: "",
      timezone: TIMEZONES?.[0] ?? "UTC",
      logo: null,
    });
    setLogoPreview(null);
    setCurrentStep(1);
    setCreatedFranchiseId(null);
    setSelectedBusinessIds(new Set());
    setSearchInput("");
    setSearchTerm("");
    setCurrentPage(1);
    onClose();
  };

  const handleBusinessToggle = async (businessId) => {
    const isSelected = selectedBusinessIds.has(businessId);

    try {
      await assignBusinessMutation.mutateAsync({
        businessId,
        franchiseId: isSelected ? null : createdFranchiseId,
      });

      setSelectedBusinessIds((prev) => {
        const newSet = new Set(prev);
        if (isSelected) {
          newSet.delete(businessId);
        } else {
          newSet.add(businessId);
        }
        return newSet;
      });

      toast.success(
        `Business ${isSelected ? "removed from" : "assigned to"} franchise`,
      );
    } catch (error) {
      console.error("Business assignment error:", error);
      toast.error("Failed to update business assignment");
    }
  };

  const handleSelectAll = () => {
    const allBusinessIds = new Set(businesses.map((b) => b.id));

    const alreadyAllSelected =
      allBusinessIds.size > 0 &&
      Array.from(allBusinessIds).every((id) => selectedBusinessIds.has(id));

    if (alreadyAllSelected) {
      toast.info("All businesses are already selected");
      return;
    }

    setSelectedBusinessIds(allBusinessIds);

    Promise.all(
      businesses
        .filter((b) => !selectedBusinessIds.has(b.id))
        .map((business) =>
          assignBusinessMutation.mutateAsync({
            businessId: business.id,
            franchiseId: createdFranchiseId,
          }),
        ),
    )
      .then(() => {
        toast.success("All businesses assigned successfully");
      })
      .catch(() => {
        toast.error("Failed to assign some businesses");
      });
  };

  const handleSelectNone = () => {
    const alreadyNoneSelected = selectedBusinessIds.size === 0;

    if (alreadyNoneSelected) {
      toast.info("No businesses are selected");
      return;
    }

    const idsToUnselect = Array.from(selectedBusinessIds);
    setSelectedBusinessIds(new Set());

    Promise.all(
      businesses
        .filter((b) => idsToUnselect.includes(b.id))
        .map((business) =>
          assignBusinessMutation.mutateAsync({
            businessId: business.id,
            franchiseId: null,
          }),
        ),
    )
      .then(() => {
        toast.success("All businesses unassigned successfully");
      })
      .catch(() => {
        toast.error("Failed to unassign some businesses");
      });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleClearSearch = () => {
    setSearchInput("");
  };

  // Callback to update businesses when selecting all
  const handleUpdateBusinesses = async (businessArray) => {
    if (!createdFranchiseId) {
      console.error("Cannot update businesses: franchise not created yet");
      return;
    }

    // Get all businesses that should be assigned
    const businessesToAssign = businessArray.filter(
      (id) => !selectedBusinessIds.has(id),
    );
    // Get all businesses that should be unassigned
    const businessesToUnassign = Array.from(selectedBusinessIds).filter(
      (id) => !businessArray.includes(id),
    );

    try {
      // Assign new businesses
      await Promise.all(
        businessesToAssign.map((businessId) =>
          assignBusinessMutation.mutateAsync({
            businessId,
            franchiseId: createdFranchiseId,
          }),
        ),
      );

      // Unassign businesses that are no longer selected
      await Promise.all(
        businessesToUnassign.map((businessId) =>
          assignBusinessMutation.mutateAsync({
            businessId,
            franchiseId: null,
          }),
        ),
      );

      toast.success("Businesses updated successfully");
    } catch (error) {
      console.error("Error updating businesses:", error);
      toast.error("Failed to update some businesses");
      throw error; // Re-throw to let BusinessesTab handle the reversion
    }
  };

  const handleFinish = () => {
    handleApiSuccess(
      `Brand created with ${selectedBusinessIds.size} business(es) assigned`,
      toast,
      {
        title: "Success",
      },
    );
    handleCancel();
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title={currentStep === 1 ? "Create New Brand" : "Assign Businesses"}
      subtitle={
        currentStep === 2
          ? "Select which businesses will be part of this franchise"
          : null
      }
      maxWidth="max-w-3xl"
    >
      {currentStep === 1 ? (
        <form
          className="ordering-brands-admin ordering-brands-modal"
          onSubmit={handleSubmit}
        >
          <div className="space-y-6">
            {/* Logo */}
            <div>
              <label
                id="create-brand-logo-label"
                className="text-foreground mb-2 block text-sm font-medium"
              >
                Logo
              </label>
              <p className="text-muted-foreground mb-3 text-xs">
                Accepted formats: PNG, JPG, JPEG, WEBP (Max 512KB)
              </p>
              <div className="flex justify-center">
                <label className="inline-block cursor-pointer">
                  <span id="create-brand-logo-action" className="sr-only">
                    Click to upload logo
                  </span>
                  <div className="border-border bg-card hover:bg-secondary relative flex h-32 w-32 items-center justify-center rounded-lg border transition-colors">
                    {logoPreview ? (
                      <>
                        <img
                          src={logoPreview}
                          alt="Preview"
                          className="max-h-full max-w-full rounded-lg object-contain"
                        />
                        {/* Overlay - Dots and Image Icon - Always visible */}
                        <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/40">
                          <div className="flex items-center space-x-1">
                            <Icon name="Image" className="h-5 w-5 text-white" />
                            <Icon
                              name="MoreVertical"
                              className="h-5 w-5 text-white"
                            />
                          </div>
                        </div>
                        {/* Clickable overlay */}
                        <input
                          type="file"
                          aria-labelledby="create-brand-logo-label create-brand-logo-action"
                          accept="image/png,image/jpg,image/jpeg,image/webp"
                          onChange={handleLogoChange}
                          className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
                          disabled={createMutation.isPending}
                        />
                      </>
                    ) : (
                      <>
                        <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/40">
                          <div className="flex items-center space-x-1">
                            <Icon name="Image" className="h-7 w-7 text-white" />
                            <Icon
                              name="MoreVertical"
                              className="h-7 w-7 text-white"
                            />
                          </div>
                        </div>
                        <input
                          type="file"
                          aria-labelledby="create-brand-logo-label create-brand-logo-action"
                          accept="image/png,image/jpg,image/jpeg,image/webp"
                          onChange={handleLogoChange}
                          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                          disabled={createMutation.isPending}
                        />
                      </>
                    )}
                  </div>
                </label>
              </div>
            </div>

            {/* Name */}
            <div>
              <label
                htmlFor="create-brand-name"
                className="text-foreground mb-2 block text-sm font-medium"
              >
                Name <span className="text-destructive">*</span>
              </label>
              <input
                id="create-brand-name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                disabled={createMutation.isPending}
                className="border-border bg-card focus:ring-ring text-foreground w-full rounded-lg border px-3 py-2 placeholder-gray-500 focus:ring-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Enter brand name"
                required
              />
            </div>

            {/* Slug */}
            <div>
              <label
                htmlFor="create-brand-slug"
                className="text-foreground mb-2 block text-sm font-medium"
              >
                Slug <span className="text-destructive">*</span>
              </label>
              <input
                id="create-brand-slug"
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleInputChange}
                disabled={createMutation.isPending}
                className="border-border bg-card focus:ring-ring text-foreground w-full rounded-lg border px-3 py-2 placeholder-gray-500 focus:ring-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="brand-slug"
                required
              />
              <p className="text-muted-foreground mt-1 text-xs">
                URL-friendly version of the brand name
              </p>
            </div>

            {/* Internal Name */}
            <div>
              <label
                htmlFor="create-brand-internal-name"
                className="text-foreground mb-2 block text-sm font-medium"
              >
                Internal Name
              </label>
              <input
                id="create-brand-internal-name"
                type="text"
                name="internal_name"
                value={formData.internal_name}
                onChange={handleInputChange}
                disabled={createMutation.isPending}
                className="border-border bg-card focus:ring-ring text-foreground w-full rounded-lg border px-3 py-2 placeholder-gray-500 focus:ring-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Internal identifier"
              />
              <p className="text-muted-foreground mt-1 text-xs">
                Franchise name for internal use only
              </p>
            </div>

            {/* Timezone */}
            <div>
              <label
                htmlFor="create-brand-timezone"
                className="text-foreground mb-2 block text-sm font-medium"
              >
                Timezone
              </label>
              <div className="relative">
                <NativeSelect
                  id="create-brand-timezone"
                  name="timezone"
                  value={formData.timezone ?? ""}
                  onChange={(e) =>
                    handleInputChange({
                      target: { name: "timezone", value: e.target.value },
                    })
                  }
                  disabled={createMutation.isPending}
                  className="h-11 w-full"
                  aria-label="Select brand timezone"
                >
                  {(TIMEZONES || []).map((timezone) => (
                    <option key={timezone} value={timezone}>
                      {timezone}
                    </option>
                  ))}
                </NativeSelect>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 pt-6">
            <Button
              variant="ghost"
              type="button"
              onClick={handleCancel}
              disabled={createMutation.isPending}
              className="bg-card hover:bg-muted disabled:bg-muted text-foreground rounded-lg px-4 py-2 transition-colors disabled:cursor-not-allowed"
            >
              Cancel
            </Button>
            <Button
              variant="default"
              type="submit"
              disabled={
                createMutation.isPending ||
                !formData.name.trim() ||
                !formData.slug.trim()
              }
              aria-busy={createMutation.isPending}
              className="px-4 py-2 disabled:cursor-not-allowed"
            >
              {createMutation.isPending ? (
                <div className="flex items-center space-x-2">
                  <Icon name="Loader" className="h-4 w-4 animate-spin" />
                  <span>Creating...</span>
                </div>
              ) : (
                "Create Brand"
              )}
            </Button>
          </div>
        </form>
      ) : (
        <div className="ordering-brands-admin ordering-brands-modal space-y-6">
          <BusinessesTab
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            handleClearSearch={handleClearSearch}
            handleSelectAll={handleSelectAll}
            handleSelectNone={handleSelectNone}
            handleBusinessToggle={handleBusinessToggle}
            selectedBusinessIds={selectedBusinessIds}
            setSelectedBusinessIds={setSelectedBusinessIds}
            isLoadingBusinesses={isLoadingBusinesses}
            assignBusinessMutation={assignBusinessMutation}
            businesses={businesses}
            currentPage={currentPage}
            pagination={pagination}
            handlePageChange={handlePageChange}
            searchTerm={searchTerm}
            onUpdateBusinesses={
              createdFranchiseId ? handleUpdateBusinesses : undefined
            }
            initialSelectedBusinessIds={[]}
          />

          <div className="border-border flex items-center justify-between border-t pt-6">
            <Button
              variant="ghost"
              type="button"
              onClick={handleCancel}
              className="bg-card hover:bg-muted text-foreground rounded-lg px-4 py-2 transition-colors"
            >
              Skip & Close
            </Button>
            <Button
              variant="default"
              type="button"
              onClick={handleFinish}
              disabled={assignBusinessMutation.isPending}
              aria-busy={assignBusinessMutation.isPending}
              className="px-4 py-2 disabled:cursor-not-allowed"
            >
              {assignBusinessMutation.isPending ? (
                <div className="flex items-center space-x-2">
                  <Icon name="Loader" className="h-4 w-4 animate-spin" />
                  <span>Saving...</span>
                </div>
              ) : (
                "Finish"
              )}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default CreateBrandModal;
